// Complete rendering pipeline with space-aware transforms

import { SpaceAwareRenderer, RenderContext } from './SpaceAwareRenderer';
import { BackendPlanValidator, BackendLayoutPlan, FrontendRequest } from './BackendPlanValidator';
import { ValidationFallback } from './ValidationFallback';
import { PaperDimensions } from './PaperCanvas';
import { FontParityIntegration, FontLoadingConfig } from './FontParityIntegration';
import { MultiPageRenderer } from './MultiPageRenderer';
import { MultiPageCoordinates, MultiPageContext } from './MultiPageCoordinates';

export class RenderingPipeline {
  private static fontParityIntegration: FontParityIntegration | null = null;

  /**
   * Initialize font parity integration
   */
  static initializeFontParity(config: FontLoadingConfig): void {
    this.fontParityIntegration = new FontParityIntegration(config);
  }

  /**
   * Create multi-page context from backend layout plans
   */
  static createMultiPageContext(
    plans: BackendLayoutPlan[],
    viewportDimensions: { width: number; height: number },
    scrollOffset: { x: number; y: number } = { x: 0, y: 0 },
    pageGap: number = 20
  ): MultiPageContext {
    const backendPages = plans.map(plan => ({
      width: plan.pageSizePx.width,
      height: plan.pageSizePx.height,
      contentRect: plan.contentRectPx
    }));

    return MultiPageCoordinates.createMultiPageContext(
      backendPages,
      viewportDimensions,
      scrollOffset,
      pageGap
    );
  }

  /**
   * Complete rendering pipeline with validation and fallback
   */
  static async render(
    ctx: CanvasRenderingContext2D,
    plan: BackendLayoutPlan | null,
    request: FrontendRequest,
    paperDimensions: PaperDimensions,
    documentText: string,
    options?: {
      multiPageContext?: MultiPageContext;
      enableMultiPage?: boolean;
      enableLocalTextOverride?: boolean;
      localText?: string;
    }
  ): Promise<void> {
    // Clear canvas while preserving transform
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to identity for clearing
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height); // Clear entire canvas buffer
    ctx.restore(); // Restore DPI scaling transform

    // Render paper background
    SpaceAwareRenderer.renderPaperBackground(
      ctx,
      paperDimensions.paperWidth,
      paperDimensions.paperHeight
    );

    if (!plan) {
      // No backend plan - use fallback
      console.log('🔍 [RenderingPipeline] No backend plan received - plan is:', plan);
      console.log('🔍 [RenderingPipeline] TIMING DEBUG - This suggests PaperCanvas rendered before SyncManager layout-plan-updated event');
      ValidationFallback.logFallbackActivation('No backend plan available', []);
      ValidationFallback.renderFallback(ctx, documentText, paperDimensions);
      return;
    }


    // DEBUG: Enable validation with detailed error logging to identify root cause
    const validation = BackendPlanValidator.validateComplete(plan, request);
    
    if (!validation.isValid) {
      // Log detailed validation errors for debugging
      console.error('🚨 [RenderingPipeline] Backend plan validation failed - DEBUGGING:', {
        errorCount: validation.errors.length,
        errors: validation.errors,
        warnings: validation.warnings,
        validationTime: validation.validationTime,
        planSample: {
          hasPageSize: !!plan.pageSizePx,
          pageSizePx: plan.pageSizePx,
          hasContentRect: !!plan.contentRectPx,
          contentRectPx: plan.contentRectPx,
          hasFontFamily: !!plan.fontFamily,
          fontFamily: plan.fontFamily,
          fontSizePx: plan.fontSizePx,
          dpi: plan.dpi,
          hasLines: !!plan.pages?.[0]?.frames?.[0]?.lines,
          lineCount: plan.pages?.[0]?.frames?.[0]?.lines?.length || 0,
          firstLineStructure: plan.pages?.[0]?.frames?.[0]?.lines?.[0] ? {
            baselineY_pagePx: plan.pages[0].frames[0].lines[0].baselineY_pagePx,
            runsCount: plan.pages[0].frames[0].lines[0].runs?.length || 0,
            firstRun: plan.pages[0].frames[0].lines[0].runs?.[0] ? {
              text: plan.pages[0].frames[0].lines[0].runs[0].text?.substring(0, 20) + '...',
              x: plan.pages[0].frames[0].lines[0].runs[0].x,
              hasCharBoxes: !!plan.pages[0].frames[0].lines[0].runs[0].charBoxes,
              charBoxCount: plan.pages[0].frames[0].lines[0].runs[0].charBoxes?.length || 0
            } : null
          } : null
        },
        requestSample: {
          fontFamily: request.fontFamily,
          fontSize: request.fontSize,
          pageWidth: request.pageWidth,
          pageHeight: request.pageHeight
        }
      });
      
      // Use fallback rendering but don't return - let it continue for now
      ValidationFallback.logFallbackActivation('Validation failure - debugging', validation.errors);
      ValidationFallback.renderFallback(ctx, documentText, paperDimensions);
      return;
    }
    

    // Prepare fonts with parity verification
    let fontPreparationResult = null;
    if (this.fontParityIntegration) {
      try {
        fontPreparationResult = await this.fontParityIntegration.prepareFontsForRendering(plan);
        if (!fontPreparationResult.success) {
          console.warn('⚠️ [RenderingPipeline] Font preparation failed:', fontPreparationResult.errors);
        }
      } catch (error) {
        console.error('❌ [RenderingPipeline] Font parity integration error:', error);
      }
    }

    // Render margin guides from backend's authoritative content rectangle
    SpaceAwareRenderer.renderMarginGuides(
      ctx,
      plan.contentRectPx,
      plan.pageSizePx.width,
      plan.pageSizePx.height
    );

    // Render text using space-aware transforms
    const renderContext: RenderContext = {
      ctx,
      contentRect: plan.contentRectPx,
      pageIndex: 0, // Single page for now
      pageHeight: plan.pageSizePx.height,
      pageGap: 0,
      fontParityIntegration: this.fontParityIntegration || undefined
    };

    // Check if multi-page rendering is enabled and context is provided
    if (options?.enableMultiPage && options?.multiPageContext) {
      // Multi-page rendering
      MultiPageRenderer.renderMultiPageDocument(ctx, [plan], options.multiPageContext);
      
      console.log('✅ [RenderingPipeline] Rendered multi-page document:', {
        planHash: plan.planHash?.slice(0, 8),
        pageCount: options.multiPageContext.pages.length,
        visiblePages: MultiPageCoordinates.getVisiblePages(options.multiPageContext).length
      });
    } else {
      // Single-page rendering with optional local text override
      if (options?.enableLocalTextOverride && options?.localText) {
        SpaceAwareRenderer.renderWithExplicitTransforms(plan, renderContext, {
          overrideText: options.localText
        });
      } else {
        // Standard backend rendering
        SpaceAwareRenderer.renderWithExplicitTransforms(plan, renderContext);
      }
      
    }
  }
}